import React, { useCallback, useEffect, useState } from "react";
import { UserResponse } from "../../app/models/responseModels/userResponse";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ExpandMore } from "@mui/icons-material";
import {
  USER_COLUMN_NAME,
  USER_FILTER_COLUMN_OPTIONS,
  USER_LABEL_NAME,
} from "./UserConstants";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";

type UserDataFilterProps = {
  userData: UserResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<UserResponse[]>>;
  filteredData: UserResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const UserDataFilter = ({
  userData,
  setFilteredData,
  filteredData,
}: UserDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [userNameOptions, setUserNameOptions] = useState<string[]>([]); //Dropdown Options
  const [userNameSelectedOptions, setUserNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [emailOptions, setEmailOptions] = useState<string[]>([]); //Dropdown Options
  const [emailSelectedOptions, setEmailSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [phoneOptions, setPhoneOptions] = useState<string[]>([]); //Dropdown Options
  const [phoneSelectedOptions, setPhoneSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [deletedOptions, setDeletedOptions] = useState<string[]>([]); //Dropdown Options
  const [deletedSelectedOptions, setDeletedSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [roleOptions, setRoleOptions] = useState<string[]>([]); //Dropdown Options
  const [roleSelectedOptions, setRoleSelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [parcelCenterOptions, setParcelCenterOptions] = useState<string[]>([]); //Dropdown Options
  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === USER_COLUMN_NAME.USER_NAME) {
        return userNameOptions;
      }
      if (columnName === USER_COLUMN_NAME.EMAIL) {
        return emailOptions;
      }
      if (columnName === USER_COLUMN_NAME.PHONE) {
        return phoneOptions;
      }
      if (columnName === USER_COLUMN_NAME.DELETED) {
        return deletedOptions;
      }
      // if (columnName === USER_COLUMN_NAME.USER_ACTIONOWNER_TYPE) {
      //   return userActionOwnerTypeOptions;
      // }
      if (columnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
        return roleOptions;
      }
      if (columnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
        return parcelCenterOptions;
      }
      return [];
    },
    [
      userNameOptions,
      emailOptions,
      phoneOptions,
      deletedOptions,
      roleOptions,
      parcelCenterOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === USER_COLUMN_NAME.USER_NAME) {
        return userNameSelectedOptions;
      }
      if (columnName === USER_COLUMN_NAME.EMAIL) {
        return emailSelectedOptions;
      }
      if (columnName === USER_COLUMN_NAME.PHONE) {
        return phoneSelectedOptions;
      }
      if (columnName === USER_COLUMN_NAME.DELETED) {
        return deletedSelectedOptions;
      }
      if (columnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
        return roleSelectedOptions;
      }
      if (columnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
        return parcelCenterSelectedOptions;
      }
      return [];
    },
    [
      userNameSelectedOptions,
      emailSelectedOptions,
      phoneSelectedOptions,
      deletedSelectedOptions,
      roleSelectedOptions,
      parcelCenterSelectedOptions,
    ]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //UserName dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.USER_NAME) {
      setUserNameOptions(
        userData
          .map((v) => v.userName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setUserNameOptions(
        filteredData
          .map((v) => v.userName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Email dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.EMAIL) {
      setEmailOptions(
        userData
          .map((v) => v.email)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setEmailOptions(
        filteredData
          .map((v) => v.email)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Phone dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.PHONE) {
      setPhoneOptions(
        userData
          .map((v) => v.phone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setPhoneOptions(
        filteredData
          .map((v) => v.phone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Deleted dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.DELETED) {
      setDeletedOptions(
        userData
          .map((v) => v.deleted==='true'? 'false' : 'true')
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setDeletedOptions(
        filteredData
          .map((v) =>  v.deleted==='true'? 'false' : 'true')
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }


    //Role dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
      const rolesList = userData.map((x) => x.assignedRoles).flat(1);
      setRoleOptions(
        rolesList
          .map((v) => v.roleName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      const rolesList = filteredData.map((x) => x.assignedRoles).flat(1);
      setRoleOptions(
        rolesList
          .map((v) => v.roleName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Parcel Center dropdown options
    if (selectedColumnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
      const parcelCenterList = userData
        .filter((x) => x.assignedParcelCenters)
        .map((x) => x.assignedParcelCenters)
        .flat(1);
      setParcelCenterOptions(
        parcelCenterList
          .map((v) => v.parcelCenterName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      const parcelCenterList = filteredData
        .filter((x) => x.assignedParcelCenters)
        .map((x) => x.assignedParcelCenters)
        .flat(1);
      setParcelCenterOptions(
        parcelCenterList
          .map((v) => v.parcelCenterName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, userData]);

  useEffect(() => {
    let newData = [...userData];
    //UserName Filter
    if (userNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return userNameSelectedOptions.some((s) => {
          return s === a.userName;
        });
      });
    }

    //Email Filter
    if (emailSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return emailSelectedOptions.some((s) => {
          return s === a.email;
        });
      });
    }

    //Phone Filter
    if (phoneSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return phoneSelectedOptions.some((s) => {
          return s === a.phone;
        });
      });
    }

    //Deleted Filter
    if (deletedSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return deletedSelectedOptions.some((s) => {
          return s !== a.deleted;
        });
      });
    }

    //Role Filter

    if (roleSelectedOptions.length > 0) {
      let userIds: string[] = [];

      newData.forEach((data) => {
        data.assignedRoles.forEach((role) => {
          const selectedRole = roleSelectedOptions.some(
            (x) => x === role.roleName
          );
          if (selectedRole === true) {
            userIds.push(data.id);
          }
        });
      });

      newData = newData.filter((a) => {
        return userIds.some((s) => {
          return s === a.id;
        });
      });
    }

    //Parcel Center Filter
    if (parcelCenterSelectedOptions.length > 0) {
      let userIds: string[] = [];

      newData.forEach((data) => {
        if (data.assignedParcelCenters) {
          data.assignedParcelCenters.forEach((pc) => {
            const selectedParcelCenter = parcelCenterSelectedOptions.some(
              (x) => x === pc.parcelCenterName
            );
            if (selectedParcelCenter === true) {
              userIds.push(data.id);
            }
          });
        }
      });

      newData = newData.filter((a) => {
        return userIds.some((s) => {
          return s === a.id;
        });
      });
    }

    setFilteredData(newData);
  }, [
    userData,
    setFilteredData,
    userNameSelectedOptions,
    emailSelectedOptions,
    phoneSelectedOptions,
    deletedSelectedOptions,
    roleSelectedOptions,
    parcelCenterSelectedOptions,
  ]);

  // ***** Handlers *****
  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === USER_COLUMN_NAME.USER_NAME) {
      return setUserNameSelectedOptions([]);
    }
    if (removedColumnName === USER_COLUMN_NAME.EMAIL) {
      return setEmailSelectedOptions([]);
    }
    if (removedColumnName === USER_COLUMN_NAME.PHONE) {
      return setPhoneSelectedOptions([]);
    }
    if (removedColumnName === USER_COLUMN_NAME.DELETED) {
      return setDeletedSelectedOptions([]);
    }
    if (removedColumnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
      return setRoleSelectedOptions([]);
    }
    if (removedColumnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
      setParcelCenterSelectedOptions([]);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === USER_COLUMN_NAME.USER_NAME) {
        setUserNameSelectedOptions([]);
      }
      if (oldColumnName === USER_COLUMN_NAME.EMAIL) {
        setEmailSelectedOptions([]);
      }
      if (oldColumnName === USER_COLUMN_NAME.PHONE) {
        setPhoneSelectedOptions([]);
      }
      if (oldColumnName === USER_COLUMN_NAME.DELETED) {
        setDeletedSelectedOptions([]);
      }
      if (oldColumnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
        setRoleSelectedOptions([]);
      }
      if (oldColumnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
        setParcelCenterSelectedOptions([]);
      }
    }
    if (value === USER_COLUMN_NAME.USER_NAME) {
      newInputList[index].displayColumnName = USER_LABEL_NAME.USER_NAME;
    }
    if (value === USER_COLUMN_NAME.EMAIL) {
      newInputList[index].displayColumnName = USER_LABEL_NAME.EMAIL;
    }
    if (value === USER_COLUMN_NAME.PHONE) {
      newInputList[index].displayColumnName = USER_LABEL_NAME.PHONE;
    }
    if (value === USER_COLUMN_NAME.DELETED) {
      newInputList[index].displayColumnName = USER_LABEL_NAME.DELETED;
    }
    if (value === USER_COLUMN_NAME.ASSIGNED_ROLES) {
      newInputList[index].displayColumnName = USER_LABEL_NAME.ASSIGNED_ROLES;
    }
    if (value === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
      newInputList[index].displayColumnName =
        USER_LABEL_NAME.ASSIGNED_PARCEL_CENTERS;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === USER_COLUMN_NAME.USER_NAME) {
      return setUserNameSelectedOptions(value);
    }
    if (columnName === USER_COLUMN_NAME.EMAIL) {
      return setEmailSelectedOptions(value);
    }
    if (columnName === USER_COLUMN_NAME.PHONE) {
      return setPhoneSelectedOptions(value);
    }
    if (columnName === USER_COLUMN_NAME.DELETED) {
      return setDeletedSelectedOptions(value);
    }
    if (columnName === USER_COLUMN_NAME.ASSIGNED_ROLES) {
      return setRoleSelectedOptions(value);
    }
    if (columnName === USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS) {
      return setParcelCenterSelectedOptions(value);
    }
  };

  const handleReset = (e: any) => {
    setUserNameSelectedOptions([]);
    setEmailSelectedOptions([]);
    setPhoneSelectedOptions([]);
    setDeletedSelectedOptions([]);
    setRoleSelectedOptions([]);
    setParcelCenterSelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search User</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {USER_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <AutoCompleteFilter
                    columnName={inputList[index].columnName}
                    displayColumnName={inputList[index].displayColumnName}
                    options={getFilterOptions(inputList[index].columnName)}
                    value={getSelectedOptions(inputList[index].columnName)}
                    onChange={(event: any, selectedValue: string[]) => {
                      handleSelectedOption(
                        event,
                        inputList[index].columnName,
                        selectedValue
                      );
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default UserDataFilter;
